<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

const previewElement = ref(null);
const time = ref('morning');
const phase = ref('lock');
const reducedMotion = ref(false);
const paused = ref(false);
const clock = computed(() => time.value === 'morning' ? '7:30' : '8:00');
const greeting = computed(() => time.value === 'morning'
  ? 'Good morning! Here’s what’s coming up today.'
  : 'A quick look at tomorrow before you call it a night.');
let timers = [];
let media;
let observer;
function clearTimers() {
  timers.forEach(clearTimeout);
  timers = [];
}
function openMessage() {
  clearTimers();
  phase.value = 'message';
  paused.value = false;
}
function replay(value = time.value) {
  clearTimers();
  time.value = value;
  paused.value = false;
  phase.value = reducedMotion.value ? 'message' : 'lock';
  if (reducedMotion.value) return;
  timers.push(setTimeout(() => { phase.value = 'notification'; }, 900));
  timers.push(setTimeout(openMessage, 3600));
}
function togglePause() {
  if (paused.value) {
    paused.value = false;
    if (phase.value === 'lock') {
      timers.push(setTimeout(() => { phase.value = 'notification'; }, 500));
      timers.push(setTimeout(openMessage, 3200));
    } else timers.push(setTimeout(openMessage, 2000));
  } else {
    clearTimers();
    paused.value = true;
  }
}
function updateMotion() {
  reducedMotion.value = media.matches;
  if (reducedMotion.value) openMessage();
}
onMounted(() => {
  media = window.matchMedia('(prefers-reduced-motion: reduce)');
  reducedMotion.value = media.matches;
  media.addEventListener('change', updateMotion);
  if (reducedMotion.value) {
    openMessage();
  } else if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        observer.disconnect();
        replay();
      }
    }, { threshold: 0.35 });
    observer.observe(previewElement.value);
  } else replay();
});
onBeforeUnmount(() => {
  clearTimers();
  observer?.disconnect();
  media?.removeEventListener('change', updateMotion);
});
</script>

<template>
  <div ref="previewElement" class="digest-preview">
    <div class="demo-controls">
      <div class="time-switch" role="group" aria-label="Example digest time">
        <button :aria-pressed="time === 'morning'" @click="replay('morning')">Morning</button>
        <button :aria-pressed="time === 'night'" @click="replay('night')">Night</button>
      </div>
      <button v-if="phase !== 'message' && !reducedMotion" class="playback" @click="togglePause">{{ paused ? 'Resume' : 'Pause' }}</button>
      <button v-else class="playback" @click="replay()" :disabled="reducedMotion">Replay</button>
    </div>

    <div class="phone" :class="[{ 'is-open': phase === 'message', 'is-night': time === 'night' }, { 'reduce-motion': reducedMotion }]" aria-label="Example phone receiving a BetterConnect text">
      <div class="phone-screen">
        <div class="wallpaper"></div>
        <div class="status-bar" aria-hidden="true">
          <span>{{ clock }}</span><span class="island"></span>
          <svg width="49" height="13" viewBox="0 0 49 13" fill="currentColor"><path d="M0 9h3v4H0zm5-3h3v7H5zm5-3h3v10h-3zm5-3h3v13h-3z"/><rect x="24" y="2" width="21" height="10" rx="3" fill="none" stroke="currentColor"/><rect x="26" y="4" width="17" height="6" rx="1"/><path d="M47 5h2v4h-2z"/></svg>
        </div>

        <div class="lock-screen" :inert="phase === 'message'">
          <svg class="lock" viewBox="0 0 16 20" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><rect x="2" y="8" width="12" height="10" rx="3" fill="currentColor" stroke="none"/><path d="M4.5 8V5a3.5 3.5 0 0 1 7 0v3"/></svg>
          <p class="lock-date">{{ time === 'morning' ? 'Monday, November 2' : 'Sunday, November 1' }}</p>
          <p class="lock-time">{{ clock }}</p>
          <button class="notification" :class="{ arrived: phase === 'notification' }" :inert="phase !== 'notification'" @click="openMessage" aria-label="Open BetterConnect text notification">
            <span class="messages-icon" aria-hidden="true"><svg viewBox="0 0 28 28" fill="white"><path d="M24 13c0 5-4.5 8.5-10 8.5-1.3 0-2.5-.2-3.6-.6L5 23l1.3-4C4.2 17.5 3 15.4 3 13c0-4.8 4.7-8.5 10.5-8.5S24 8.2 24 13Z"/></svg></span>
            <span class="notification-copy"><span class="notification-title"><b>BetterConnect</b><small>now</small></span><span>{{ greeting }}</span></span>
          </button>
          <p class="open-hint" :class="{ visible: phase === 'notification' }">{{ paused ? 'Tap the notification to open.' : 'Opening your digest…' }}</p>
        </div>

        <div class="conversation" :inert="phase !== 'message'" :aria-hidden="phase !== 'message'">
          <div class="contact-header"><span class="back-chevron" aria-hidden="true">‹</span><span class="contact-avatar" aria-hidden="true">BC</span><b>BetterConnect <span aria-hidden="true">›</span></b></div>
          <div class="conversation-body">
            <p class="message-date">Text Message · SMS<br /><b>{{ time === 'morning' ? 'Today' : 'Sunday' }} {{ clock }} {{ time === 'morning' ? 'AM' : 'PM' }}</b></p>
            <div class="sms-bubble"><p>{{ greeting }}</p><p><b>{{ time === 'morning' ? 'Due today' : 'Due tomorrow' }}</b><br />Biology: Cell division lab report<br />English 11: Read chapters 6–8</p><p>{{ time === 'morning' ? 'First up' : 'Tomorrow starts with' }}: Algebra 2 at 8:50.</p></div>
          </div>
          <div class="composer" aria-hidden="true"><span>+</span><div>Text Message <svg viewBox="0 0 16 20" fill="none" stroke="currentColor" stroke-width="1.6"><rect x="5" y="1" width="6" height="11" rx="3"/><path d="M2 8v2a6 6 0 0 0 12 0V8M8 16v3m-3 0h6"/></svg></div></div>
        </div>
        <span class="home-indicator" aria-hidden="true"></span>
      </div>
    </div>
    <p class="demo-caption" aria-live="polite">{{ phase === 'message' ? 'A sample digest, opened in Messages.' : paused ? 'Animation paused.' : 'A text arrives. Your day is ready.' }}</p>
  </div>
</template>

<style scoped>
.digest-preview {
  width: 100%;
  max-width: 350px;
  margin: auto;
}
.demo-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
}
.time-switch {
  display: flex;
  padding: 3px;
  border-radius: 11px;
  background: var(--edge);
}
.time-switch button, .playback {
  min-height: 40px;
  padding: 8px 13px;
  font-size: 12px;
  border-radius: 8px;
  color: var(--muted);
}
.time-switch button[aria-pressed=true] {
  background: var(--card);
  color: var(--ink);
  box-shadow: 0 1px 4px #0002;
}
.playback {
  color: var(--ink);
}
.playback:disabled {
  opacity: .4;
}
button:focus-visible {
  outline: 3px solid #a7a5ff;
  outline-offset: 4px;
}
.phone {
  width: 280px;
  max-width: 100%;
  margin: auto;
  border-radius: 43px;
  padding: 7px;
  background: #141418;
  border: 1px solid #797981;
  box-shadow: 0 0 0 2px #333338, 0 20px 32px -12px #0005, inset 0 0 0 2px #44444a;
}
.phone-screen {
  position: relative;
  height: 548px;
  overflow: hidden;
  border-radius: 35px;
  background: #000;
  color: #fff;
  isolation: isolate;
}
.wallpaper {
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at 10% 100%, #e9b9a3 0, transparent 57%), radial-gradient(ellipse at 95% 70%, #a999da 0, transparent 65%), linear-gradient(160deg,#303954,#676785 65%,#c8a4a6);
  transition: filter .6s;
}
.is-night .wallpaper {
  filter: brightness(.55) saturate(1.4);
}
.status-bar {
  position: absolute;
  z-index: 5;
  left: 22px;
  right: 20px;
  top: 13px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11px;
  font-weight: 650;
  height: 23px;
}
.island {
  position: absolute;
  width: 82px;
  height: 23px;
  border-radius: 20px;
  background: #000;
  left: 50%;
  transform: translateX(-50%);
}
.lock-screen {
  position: absolute;
  inset: 0;
  padding-top: 55px;
  text-align: center;
  transition: opacity .45s, transform .65s cubic-bezier(.2,.8,.2,1), filter .6s;
}
.lock {
  width: 15px;
  height: 19px;
  margin: 0 auto 10px;
}
.lock-date {
  font-size: 15px;
  font-weight: 550;
  color: #fffddd;
}
.lock-time {
  font-size: 72px;
  line-height: 1.12;
  font-weight: 600;
  letter-spacing: -3px;
  color: #fffddd;
}
.notification {
  display: flex;
  gap: 9px;
  width: calc(100% - 20px);
  margin: 40px 10px 0;
  border-radius: 19px;
  padding: 14px 12px;
  text-align: left;
  background: #e6e5efc9;
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid #ffffff45;
  box-shadow: 0 5px 18px #17182920;
  color: #20202a;
  opacity: 0;
  transform: translateY(-20px) scale(.94);
  transition: opacity .3s, transform .6s cubic-bezier(.16,1.15,.3,1);
}
.notification.arrived {
  opacity: 1;
  transform: translateY(0) scale(1);
}
.messages-icon {
  width: 29px;
  height: 29px;
  border-radius: 7px;
  background: linear-gradient(#66e77c,#24ba42);
  flex-shrink: 0;
}
.messages-icon svg {
  width: 100%;
  height: 100%;
}
.notification-copy {
  font-size: 11px;
  line-height: 1.45;
}
.notification-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 3px;
}
.notification-title b {
  font-weight: 650;
}
.notification-title small {
  font-size: 9px;
  color: #53535d;
}
.open-hint {
  font-size: 10px;
  margin-top: 15px;
  opacity: 0;
  transition: opacity .4s .6s;
  color: #fffd;
}
.open-hint.visible {
  opacity: 1;
}
.conversation {
  position: absolute;
  inset: 0;
  background: #000;
  opacity: 0;
  transform: scale(.88) translateY(35px);
  transform-origin: 50% 52%;
  transition: transform .65s cubic-bezier(.22,1,.36,1), opacity .35s;
}
.is-open .conversation {
  opacity: 1;
  transform: scale(1) translateY(0);
}
.is-open .lock-screen {
  opacity: 0;
  transform: scale(1.1);
  filter: blur(8px);
  pointer-events: none;
}
.contact-header {
  padding: 49px 0 12px;
  background: #19191b;
  border-bottom: 1px solid #303033;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
}
.contact-avatar {
  display: grid;
  place-items: center;
  width: 37px;
  height: 37px;
  background: linear-gradient(#a7a7b0,#777781);
  border-radius: 50%;
  color: white;
  font-size: 15px;
  font-weight: 500;
}
.contact-header b {
  font-size: 10px;
  font-weight: 500;
}
.contact-header b span {
  color: #aaa;
  margin-left: 3px;
}
.back-chevron {
  position: absolute;
  left: 16px;
  top: 59px;
  color: #418cff;
  font-size: 35px;
  line-height: 1;
}
.conversation-body {
  padding: 14px 12px;
}
.message-date {
  text-align: center;
  font-size: 9px;
  line-height: 1.6;
  color: #959599;
  margin-bottom: 14px;
}
.message-date b {
  font-weight: 500;
}
.sms-bubble {
  position: relative;
  background: #272729;
  padding: 12px 13px;
  border-radius: 17px;
  max-width: 94%;
  font-size: 12px;
  line-height: 1.45;
  opacity: 0;
  transform: translateY(12px);
  transition: opacity .3s .2s, transform .5s .2s cubic-bezier(.2,.8,.2,1);
}
.sms-bubble::before {
  content: '';
  position: absolute;
  bottom: 0;
  left: -5px;
  width: 13px;
  height: 16px;
  background: #272729;
  border-bottom-right-radius: 12px;
}
.sms-bubble p {
  position: relative;
}
.sms-bubble p + p {
  margin-top: 12px;
}
.sms-bubble b {
  font-weight: 600;
}
.is-open .sms-bubble {
  opacity: 1;
  transform: translateY(0);
}
.composer {
  position: absolute;
  bottom: 28px;
  left: 13px;
  right: 13px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #88888d;
}
.composer > span {
  font-size: 26px;
  line-height: 28px;
  width: 28px;
  text-align: center;
  border-radius: 50%;
  background: #252527;
}
.composer > div {
  border: 1px solid #404045;
  border-radius: 18px;
  padding: 7px 11px;
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 11px;
}
.composer svg {
  width: 12px;
  height: 16px;
}
.home-indicator {
  position: absolute;
  z-index: 6;
  bottom: 8px;
  width: 90px;
  height: 4px;
  border-radius: 5px;
  background: #fff;
  left: 50%;
  transform: translateX(-50%);
}
.demo-caption {
  color: var(--muted);
  text-align: center;
  font-size: 10px;
  margin-top: 17px;
}
@media (prefers-reduced-motion: reduce) { *, *::before { transition: none !important; } }
.reduce-motion *, .reduce-motion *::before {
  transition: none !important;
}
</style>
